import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Switch,
  Text
} from '@chakra-ui/react'
import { Dispatch, SetStateAction } from 'react'

import { FormField } from '../../../../components/FormField'
import { Icon } from '../../../../components/Icon'
import { SectionTitle } from '../../../../components/SectionCard'
import { Tooltip } from '../../../../components/Tooltip'
import VideoFileInput from '../../../../components/VideoFileInput/VideoFileInput'
import { ITestimonialSlot } from '../../../../services/Event/types'
import { HELP_FORM_BLOCKED_HINT, HELP_FORM_RULE_TEXT } from '../helpForm'
import { IEventForm } from './eventFormShared'

interface IProps {
  form: IEventForm
  setForm: Dispatch<SetStateAction<IEventForm>>
  fieldErrors: Record<string, string[]>
  saving: boolean
  helpFormAllowed: boolean
  addTestimonialSlot: () => void
  updateTestimonialSlot: (index: number, patch: Partial<ITestimonialSlot>) => void
  removeTestimonialSlot: (index: number) => void
}

export function EngagementTab({
  form,
  setForm,
  fieldErrors,
  saving,
  helpFormAllowed,
  addTestimonialSlot,
  updateTestimonialSlot,
  removeTestimonialSlot
}: IProps) {
  return (
    <>
      <Box display="flex" flexDirection="column" gap={2}>
        <Box display="flex" alignItems="center" gap={3}>
          <FormField label="Incluir vídeo de depoimento" htmlFor="use_testimonial" mb={0}>
            <Switch
              id="use_testimonial"
              isChecked={form.use_testimonial}
              onChange={(e) =>
                setForm({
                  ...form,
                  use_testimonial: e.target.checked,
                  testimonial_title: e.target.checked ? (form.testimonial_title ?? '') : null,
                  testimonials: e.target.checked
                    ? form.testimonials.length > 0
                      ? form.testimonials
                      : [{ _id: crypto.randomUUID(), file: null, existingUrl: null }]
                    : []
                })
              }
              isDisabled={saving}
            />
          </FormField>
        </Box>

        {form.use_testimonial && (
          <Flex direction="column" gap={3}>
            <FormField label="Título do depoimento" error={fieldErrors['testimonial_title']?.[0]}>
              <Input
                value={form.testimonial_title ?? ''}
                onChange={(e) => setForm({ ...form, testimonial_title: e.target.value })}
                placeholder="Ex: O que nossos alunos dizem"
                disabled={saving}
              />
            </FormField>
            <FormField label="Vídeos de depoimento (MP4)" error={fieldErrors['testimonial_videos']?.[0]}>
              <FormHelperText mt={0} mb={2}>
                Adicione até 3 vídeos MP4 (até 100MB cada). Com mais de um, eles aparecem na vertical, lado a lado, no
                site.
              </FormHelperText>
              <Flex direction="column" gap={4}>
                {form.testimonials.map((slot, index) => (
                  <Box key={slot._id} borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
                    <Flex justify="space-between" align="center" mb={2}>
                      <Text variant="caption">Vídeo {index + 1}</Text>
                      {form.testimonials.length > 1 && (
                        <Button
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => removeTestimonialSlot(index)}
                          disabled={saving}
                        >
                          Remover
                        </Button>
                      )}
                    </Flex>
                    <VideoFileInput
                      file={slot.file}
                      existingUrl={slot.existingUrl}
                      onChange={(file) => updateTestimonialSlot(index, { file })}
                      onRemoveExisting={() => updateTestimonialSlot(index, { file: null, existingUrl: null })}
                      isDisabled={saving}
                      maxSizeMB={100}
                      error={fieldErrors[`testimonial_videos.${index}`]}
                    />
                  </Box>
                ))}
              </Flex>
              {form.testimonials.length < 3 && (
                <Button
                  mt={3}
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  onClick={addTestimonialSlot}
                  disabled={saving}
                >
                  + Adicionar vídeo
                </Button>
              )}
            </FormField>
          </Flex>
        )}
      </Box>

      <Divider mt={5} mb={5} />
      <SectionTitle>Tire suas dúvidas</SectionTitle>

      <FormControl isInvalid={!!fieldErrors['use_help_form']} display="flex" flexDirection="column" gap={2}>
        <Box display="flex" alignItems="center" gap={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <FormLabel htmlFor="use_help_form" mb={0} me={0}>
              Exibir formulário &quot;Tire suas dúvidas&quot;
            </FormLabel>
            {/* Fora do <label> e focável: dentro dele o clique alternaria o switch, e um
                ícone solto não recebe foco — o Tooltip só abre no hover ou no foco. */}
            <Tooltip label={HELP_FORM_RULE_TEXT}>
              <Box as="span" tabIndex={0} role="img" aria-label="Ajuda" display="inline-flex" cursor="help">
                <Icon name="lni lni-information" size="sm" color="gray.400" />
              </Box>
            </Tooltip>
          </Box>
          <Switch
            id="use_help_form"
            isChecked={helpFormAllowed && form.use_help_form}
            onChange={(e) => setForm({ ...form, use_help_form: e.target.checked })}
            isDisabled={saving || !helpFormAllowed}
          />
        </Box>
        <Text variant="caption">
          Quando ativado, um botão flutuante de WhatsApp com o formulário &quot;Tire suas dúvidas&quot; aparece na
          página pública do evento.
        </Text>
        {!helpFormAllowed && (
          <Text fontSize="sm" color="orange.600">
            {HELP_FORM_BLOCKED_HINT}
          </Text>
        )}
        <FormErrorMessage>{fieldErrors['use_help_form']?.[0]}</FormErrorMessage>
      </FormControl>
    </>
  )
}
